import React from 'react';
import {
    getCallEventNotification,
    getKeyPhraseIntegrationWords,
    getLastCallPhraseSpottingScore,
    getTrainerAsrConfigErrors,
    getWhitelistPhrasesSpottingScore
} from '../../store/selectors/trainer';
import Trainer from '../../components/Trainer/Trainer';
import type { ConversionPayload } from '../../components/Trainer/Conversion/TrainerConversion';

import {
    setResultPopupVisibility,
    onManualCallEvent,
    changeCallButtonAvailability,
    userReload,
    manualOptOut,
    manualOptIn,
    manualOptStop,
    init,
    saveConversion,
    closeConversionModal
} from '../../store/actions/trainer';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getAuthUserId } from '../../store/selectors/auth';
import type { RootState } from '../../store/types';

const selectTopPanelState = (state: RootState) => ({
    prompt: state.prompt
});

const selectDetectionState = (state: RootState) => ({
    asrConfigErrors: getTrainerAsrConfigErrors(state),
    configLoaded: state.trainer.configLoaded,
    whitelist: state.trainer.whitelist,
    blacklist: state.trainer.blacklist,
    userInterfaceConfig: state.trainer.userInterfaceConfig,
    isCallStarted: state.trainer.isStarted,
    optInOn: state.trainer.optInOn,
    callHold: state.trainer.callHold,
    keyPhraseIntegrationWords: getKeyPhraseIntegrationWords(state)
});

const selectTabsState = (state: RootState) => ({
    enableTrainer: state.trainer.enableTrainer,
    enableSales: state.trainer.enableSales,
    enableEvaluations: state.trainer.enableEvaluations,
    isNotificationShown: state.trainer.isNotificationShown,
    callEventNotification: getCallEventNotification(state)
});

const selectOverlayState = (state: RootState) => ({
    whitelistPhrasesSpottingScore: getWhitelistPhrasesSpottingScore(state),
    lastCallPhraseSpottingScore: getLastCallPhraseSpottingScore(state),
    isCallResultVisible: state.trainer.isCallResultVisible,
    isLastCallResult: state.trainer.isLastCallResult,
    conversionModalOpen: state.trainer.conversionModalOpen,
    callId: state.trainer.callId,
    userId: getAuthUserId(state),
    micDeviceNotAvailable: state.trainer.micDeviceNotAvailable
});

const selectCallControlState = (state: RootState) => ({
    isManualCallButtonEnabled: state.trainer.isManualCallButtonEnabled,
    isOptOut: state.trainer.isOptOut,
    stopRecordingCustomer: state.trainer.stopRecordingCustomer
});

function TrainerContainer() {
    const dispatch = useAppDispatch();
    const stateProps = useAppSelector((state: RootState) => {
        const topPanelState = selectTopPanelState(state);
        const detectionState = selectDetectionState(state);
        const tabsState = selectTabsState(state);
        const overlayState = selectOverlayState(state);
        const callControlState = selectCallControlState(state);

        return {
            topPanelState,
            detectionState,
            tabsState,
            overlayState,
            callControlState
        };
    });

    return (
        <Trainer
            topPanelState={stateProps.topPanelState}
            detectionState={stateProps.detectionState}
            tabsState={stateProps.tabsState}
            overlayState={stateProps.overlayState}
            callControlState={stateProps.callControlState}
            trainerActions={{
                init: () => dispatch(init()),
                reload: () => dispatch(userReload()),
                closeResultPopup: () => dispatch(setResultPopupVisibility(false))
            }}
            callActions={{
                sendManualCallEvent: (isStart: boolean) => dispatch(onManualCallEvent(isStart)),
                setManualButtonDisabled: () => dispatch(changeCallButtonAvailability(false))
            }}
            optActions={{
                manualOptIn: () => dispatch(manualOptIn()),
                manualOptOut: () => dispatch(manualOptOut()),
                manualOptStop: () => dispatch(manualOptStop())
            }}
            conversionActions={{
                saveConversion: (payload: ConversionPayload) => dispatch(saveConversion(payload)),
                closeConversionModal: () => dispatch(closeConversionModal())
            }}
        />
    );
}

export default TrainerContainer;
